import { DeliveryAddress } from "@amzn/ito-client";
import { IItemVersion } from "lib/types/items";
import { useState } from "react";

import useInput from "../../../hooks/useInput";
import { AddressFormFields } from "./useChangeAddressForm";

export interface CheckoutFormFields {
  catalogItemVersion: IItemVersion;
  serialNumberForReturnDevice: string;
  businessJustification: string;
  costCenter: string;
  carCode: string;
  managerConfirmationCheckbox: boolean;

  addressDetails: AddressFormFields | undefined;
  deliveryInstructions: string;
  shippingDetailsConfirmationCheckbox: boolean;

  // Hardware Ownership and IT Services Hardware Policy
  policyConfirmationCheckbox: boolean;
}

export interface CheckoutFormProps {
  isDeviceReturnRequired: boolean;
}

export interface useCheckoutFormResult {
  items: useCheckoutFormItems;
  actions: useCheckoutFormActions;
}

export interface useCheckoutFormItems {
  formValues: CheckoutFormFields;
  selectedItemTxId: string;
}

export interface useCheckoutFormActions {
  handleInputChange: (field: keyof CheckoutFormFields, value: any, flag?: string | undefined) => void;
  createDeliveryAddress: (addressForm: AddressFormFields) => DeliveryAddress;
  hasRequiredFields: (isDeviceReturnRequired: boolean) => boolean;
  setSelectedItem: (id: string) => void;
  addCountryToDeliverToDetails: (addressForm: AddressFormFields) => unknown;
}

export const useCheckoutForm = (
  props: CheckoutFormProps = { isDeviceReturnRequired: false }
): useCheckoutFormResult => {
  const emptyVersion: IItemVersion = {
    taxonomyId: "",
    name: "",
    displayText: "",
    status: "",
    currency: "",
    amount: 0,
    logoUrl: "",
  };

  // Fill initial values
  const { formValues, handleInputChange } = useInput<CheckoutFormFields>({
    catalogItemVersion: emptyVersion,
    businessJustification: "",
    serialNumberForReturnDevice: "",
    costCenter: "",
    carCode: "",
    managerConfirmationCheckbox: false,

    addressDetails: undefined,
    deliveryInstructions: "",
    shippingDetailsConfirmationCheckbox: false,

    policyConfirmationCheckbox: false,
  });

  const [selectedItemTxId, setSelectedItemTxId] = useState("");

  const setSelectedItem = (itemId: string) => setSelectedItemTxId(itemId);

  const hasRequiredFields = (isDeviceReturnRequired: boolean) =>
    formValues.managerConfirmationCheckbox &&
    formValues.shippingDetailsConfirmationCheckbox &&
    formValues.policyConfirmationCheckbox &&
    formValues.costCenter !== "" &&
    formValues.businessJustification !== "" &&
    (isDeviceReturnRequired ? formValues.serialNumberForReturnDevice !== "" : true);

  const createDeliveryAddress = (addressForm: AddressFormFields): DeliveryAddress => {
    const address: DeliveryAddress = {
      fullName: addressForm.fullName,
      addressLineData: [addressForm.addressLine1, addressForm.addressLine2],
      country: addressForm.country,
      region: "-",
      city: addressForm.city,
      citySubdivision1: addressForm.citySubdivision1,
      postalCode: addressForm.postalCode,
      phoneNumber: `${addressForm.countryCode} ${addressForm.phoneNumber}`,
      deliveryInstructions: formValues.deliveryInstructions,
    };
    return address;
  };

  const addCountryToDeliverToDetails = (addressForm: AddressFormFields): unknown => {
    const addressWithOnlyCountry = {
      country: addressForm.countryISO3,
    };

    return addressWithOnlyCountry;
  };

  return {
    items: {
      formValues,
      selectedItemTxId,
    },
    actions: {
      handleInputChange,
      createDeliveryAddress,
      hasRequiredFields,
      setSelectedItem,
      addCountryToDeliverToDetails,
    },
  };
};
